import { ElementType, ModeType, QuestionType, type Question } from '@/constant/schema';
import type { DemoSelection } from '@/components/whiteboard-v2/LiveWhiteboard';
import type { StoredProblem } from '@/components/whiteboard-v2/types';
import { normalizeLiveTutorSubject } from '@/lib/liveTutorSubject';

type QuestionLike = Partial<Question> & {
  metadata?: Record<string, unknown> | null;
  picture?: string | null;
  support_math_blank_filling?: boolean;
};

type KeyPropertyLike = {
  id?: string | null;
  title?: string | null;
  skill_id?: string | null;
  key_property?: Record<string, unknown> | null;
};

type ExampleQuestionLike = {
  id?: string | null;
  title?: string | null;
  questions?: Record<string, unknown> | null;
};

type LearnAnswerState = 'correct' | 'incorrect' | 'none';

const IMAGE_LAYOUTS = new Set<StoredProblem['imageLayout']>([
  'multi_diagram',
  'option_images',
  'diagram_plus_options',
  'options_in_image',
]);

const PROBLEM_TYPES = new Set<NonNullable<StoredProblem['type']>>([
  'multiple_choice',
  'math_blank_filling',
  'blank_filling',
]);

export const PRACTICE_LIVE_TUTOR_PHASE1_SUBJECTS = [
  'math',
  'amc_math',
  'science',
  'history',
  'vocabulary',
  'grammar',
] as const;

const PRACTICE_LIVE_TUTOR_PHASE1_SUBJECT_SET = new Set<string>(
  PRACTICE_LIVE_TUTOR_PHASE1_SUBJECTS,
);

const LEARN_QUESTION_LIVE_TUTOR_SUBJECT_SET = new Set<string>([
  'math',
  'amc_math',
  'science',
  'history',
  'vocabulary',
  'grammar',
  'language',
  'language_arts',
  'english',
  'ela',
]);

function normalizeLiveTutorSubjectKeyLocal(subject: string | null | undefined): string {
  const normalized =
    typeof subject === 'string'
      ? subject.trim().toLowerCase().replace(/[\s-]+/g, '_')
      : '';
  if (
    normalized === 'social_studies' ||
    normalized === 'socialstudy' ||
    normalized === 'socialstudies'
  ) {
    return 'history';
  }
  return normalized;
}
function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const normalized = value
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map((item) => item.trim());
  return normalized.length > 0 ? normalized : undefined;
}

function resolveDisplayedOptions(displayedOptions: unknown): string[] | undefined {
  return asStringArray(displayedOptions);
}

function resolveProblemType(question: QuestionLike | Record<string, unknown>): StoredProblem['type'] | undefined {
  const rawType = asNonEmptyString(
    'type' in question ? question.type : undefined,
  ) ?? asNonEmptyString(
    'question_type' in question ? question.question_type : undefined,
  );

  if (rawType && PROBLEM_TYPES.has(rawType as NonNullable<StoredProblem['type']>)) {
    return rawType as StoredProblem['type'];
  }

  if ('support_math_blank_filling' in question && question.support_math_blank_filling) {
    return 'math_blank_filling';
  }

  const options = asStringArray('options' in question ? question.options : undefined);
  return options && options.length > 0 ? 'multiple_choice' : 'blank_filling';
}

function resolveProblemImages(question: QuestionLike | Record<string, unknown>) {
  const metadata =
    'metadata' in question && question.metadata && typeof question.metadata === 'object'
      ? question.metadata
      : null;

  const metadataObj = metadata as Record<string, any> | null;
  const metadataPictures = Array.isArray(metadataObj?.pictures)
    ? (metadataObj!.pictures as unknown[])
        .filter((src): src is string => typeof src === 'string' && src.trim().length > 0)
        .map((src) => src.trim())
    : [];

  const imageUrl =
    metadataPictures[0] ??
    asNonEmptyString(metadataObj?.picture) ??
    asNonEmptyString('picture' in question ? (question as any).picture : undefined);

  const imageUrls = metadataPictures.length > 1 ? metadataPictures : undefined;
  const rawLayout = asNonEmptyString(metadataObj?.image_layout);
  const imageLayout =
    rawLayout && IMAGE_LAYOUTS.has(rawLayout as StoredProblem['imageLayout'])
      ? (rawLayout as StoredProblem['imageLayout'])
      : undefined;

  return { imageUrl, imageUrls, imageLayout };
}

function resolveQuestionHtml(question: QuestionLike | Record<string, unknown>): string | undefined {
  const metadata =
    'metadata' in question && question.metadata && typeof question.metadata === 'object'
      ? question.metadata
      : null;
  const metadataObj = metadata as Record<string, unknown> | null;

  return (
    asNonEmptyString('questionHtml' in question ? (question as Record<string, unknown>).questionHtml : undefined)
    ?? asNonEmptyString('question_html' in question ? (question as Record<string, unknown>).question_html : undefined)
    ?? asNonEmptyString(metadataObj?.questionHtml)
    ?? asNonEmptyString(metadataObj?.question_html)
  );
}

function isMathSubject(subject: string | null | undefined): boolean {
  return typeof subject === 'string' && subject.toLowerCase().includes('math');
}

function isPracticeQuestionSubject(subject: string | null | undefined): boolean {
  const normalized = normalizeLiveTutorSubjectKeyLocal(subject);
  if (normalized.includes('math')) return true;
  if (normalized.includes('history') || normalized.includes('social_studies')) return true;
  if (
    normalized.includes('grammar') ||
    normalized.includes('language') ||
    normalized.includes('english') ||
    normalized.includes('ela')
  ) {
    return true;
  }
  return PRACTICE_LIVE_TUTOR_PHASE1_SUBJECT_SET.has(normalized);
}

function isLearnQuestionSubject(subject: string | null | undefined): boolean {
  const normalized = normalizeLiveTutorSubjectKeyLocal(subject);
  if (normalized.includes('math')) return true;
  if (normalized.includes('history') || normalized.includes('social_studies')) return true;
  if (
    normalized.includes('grammar') ||
    normalized.includes('language') ||
    normalized.includes('english') ||
    normalized.includes('ela')
  ) {
    return true;
  }
  return LEARN_QUESTION_LIVE_TUTOR_SUBJECT_SET.has(normalized);
}

export function isMathLearnLiveTutorEnabled(
  mode: ModeType | string | undefined,
  subject: string | null | undefined,
): boolean {
  return mode === ModeType.LEARN && isMathSubject(subject);
}

export function isLearnQuestionLiveTutorEnabled(
  mode: ModeType | string | undefined,
  subject: string | null | undefined,
): boolean {
  return mode === ModeType.LEARN && isLearnQuestionSubject(subject);
}

export function isMathPracticeLiveTutorEnabled(
  mode: ModeType | string | undefined,
  subject: string | null | undefined,
): boolean {
  return mode === ModeType.PRACTICE && isMathSubject(subject);
}

export function isPracticeQuestionLiveTutorEnabled(
  mode: ModeType | string | undefined,
  subject: string | null | undefined,
): boolean {
  return mode === ModeType.PRACTICE && isPracticeQuestionSubject(subject);
}

export function hasAnsweredLearnQuestion(
  answerState: LearnAnswerState | string | null | undefined,
): boolean {
  return answerState === 'correct' || answerState === 'incorrect';
}

export function buildQuestionCardLiveTutorProblem({
  elementId,
  elementType,
  questionId,
  question,
  subject,
  displayedOptions,
}: {
  elementId: string;
  elementType: ElementType | string;
  questionId?: string | null;
  question: QuestionLike | null;
  subject?: string | null;
  displayedOptions?: string[] | null;
}): StoredProblem | null {
  if (!question) return null;

  const problemId =
    elementType === ElementType.QUESTION_V2
      ? asNonEmptyString(questionId) ?? asNonEmptyString(question.id) ?? elementId
      : elementId;

  const { imageUrl, imageUrls, imageLayout } = resolveProblemImages(question);
  const questionHtml = resolveQuestionHtml(question);

  return {
    id: problemId,
    sourceQuestionId: asNonEmptyString(question.id),
    question: asNonEmptyString(question.question) ?? '',
    answer: asNonEmptyString(question.answer) ?? '',
    explanation: asNonEmptyString(question.explanation) ?? '',
    options: resolveDisplayedOptions(displayedOptions) ?? asStringArray(question.options),
    type: resolveProblemType(question),
    imageUrl,
    imageUrls,
    imageLayout,
    subject: normalizeLiveTutorSubject(subject),
    ...(questionHtml ? { questionHtml } : {}),
  };
}

export function buildExampleQuestionLiveTutorProblem(
  element: ExampleQuestionLike,
  subject?: string | null,
  displayedOptions?: string[] | null,
): StoredProblem | null {
  const question =
    element.questions && typeof element.questions === 'object'
      ? element.questions
      : null;
  const elementId = asNonEmptyString(element.id);

  if (!question || !elementId) return null;

  const { imageUrl, imageUrls, imageLayout } = resolveProblemImages(question);
  const questionHtml = resolveQuestionHtml(question);

  return {
    id: elementId,
    sourceQuestionId: asNonEmptyString(question.id),
    question: asNonEmptyString(question.question) ?? asNonEmptyString(element.title) ?? '',
    answer: asNonEmptyString(question.answer) ?? '',
    explanation: asNonEmptyString(question.explanation) ?? '',
    options: resolveDisplayedOptions(displayedOptions) ?? asStringArray(question.options),
    type: resolveProblemType(question),
    imageUrl,
    imageUrls,
    imageLayout,
    subject: normalizeLiveTutorSubject(subject),
    ...(questionHtml ? { questionHtml } : {}),
  };
}

export function isPracticeQuestionLiveTutorSupported(
  questionType: QuestionType | string | null | undefined,
): boolean {
  return (
    questionType === QuestionType.MULTIPLE_CHOICE ||
    questionType === QuestionType.MATH_BLANK_FILLING ||
    questionType === QuestionType.BLANK_FILLING ||
    questionType === QuestionType.GRAMMAR_FILL_IN_BLANK ||
    questionType === QuestionType.GRAMMAR_CLASSIFICATION
  );
}

export function buildPracticeQuestionLiveTutorProblem(
  question: QuestionLike | null,
  subject?: string | null,
  displayedOptions?: string[] | null,
): StoredProblem | null {
  const problemId = asNonEmptyString(question?.id);
  if (!question || !problemId) return null;

  const { imageUrl, imageUrls, imageLayout } = resolveProblemImages(question);
  const questionHtml = resolveQuestionHtml(question);

  return {
    id: problemId,
    sourceQuestionId: problemId,
    question: asNonEmptyString(question.question) ?? '',
    answer: asNonEmptyString(question.answer) ?? '',
    explanation: asNonEmptyString(question.explanation) ?? '',
    options: resolveDisplayedOptions(displayedOptions) ?? asStringArray(question.options),
    type: resolveProblemType(question),
    imageUrl,
    imageUrls,
    imageLayout,
    subject: normalizeLiveTutorSubject(subject),
    ...(questionHtml ? { questionHtml } : {}),
  };
}

export function buildKeyPropertyLiveTutorSelection(
  element: KeyPropertyLike,
  subject?: string | null,
): DemoSelection | null {
  const elementId = asNonEmptyString(element.id);
  const keyProperty =
    element.key_property && typeof element.key_property === 'object'
      ? element.key_property
      : null;

  if (!elementId || !keyProperty) return null;

  const title = asNonEmptyString(keyProperty.title) ?? asNonEmptyString(element.title);
  const content = asNonEmptyString(keyProperty.content);

  if (!title || !content) return null;

  const keyPropertyId = elementId;
  const picturePosition = asNonEmptyString(keyProperty.picture_position) === 'left' ? 'right' : 'below';

  return {
    kind: 'skill',
    skillId: asNonEmptyString(element.skill_id) ?? '',
    keyProperty: {
      id: keyPropertyId,
      title,
      content,
      examples: asNonEmptyString(keyProperty.examples),
      explanation: asNonEmptyString(keyProperty.explanation),
      picture: asNonEmptyString(keyProperty.picture),
      picturePosition,
    },
    problem: {
      id: keyPropertyId,
      question: title,
      answer: content,
      explanation: [
        content,
        asNonEmptyString(keyProperty.examples),
        asNonEmptyString(keyProperty.explanation),
      ].filter(Boolean).join(' ').trim(),
      subject: normalizeLiveTutorSubject(subject),
      sourceType: 'key_property',
    },
  };
}
